import type {
  PartnerAddressV2,
  PartnerCarrierRate,
  PartnerContact,
  PartnerContractDetail,
  PartnerOrigin,
  PartnerProcessingCutoff,
  PartnerTenant,
} from '@shipium-corp/shipium-ts-types';
import type { LinearUnit } from '../../assets/types';
import type { FulfillmentContextDomain, InjectionProfile, InjectionSite } from '../../fulfillmentContext/types';

export interface AuditAsset {
  active: boolean;
  archived: boolean;
  auditInfo: AuditInfo;
  name: string;
  urn: string;
  description?: string;
}

export interface ReturnsSettingsDetail {
  returnToAddress: AddressDetail;
  returnToContactInfo: ContactDetail;
  tags: string[];
  returnsSettings: ReturnSetting;
}

export interface FulfillmentContextDetail {
  fulfillmentContext: FulfillmentContext;
  injectionMethods: string[];
  injectionProfiles: InjectionProfileDetail[];
  augmentations: AugmentationDetail[];
  augmentationSet: AugmentationSet;
  contracts: PartnerContractDetail[];
  labelProperties: LabelPropertiesDetail;
  matchInfo: any;
  origin: OriginDetail;
  packaging: any;
  returnsSettings: ReturnsSettingsDetail;
  tags: AssetTag[];
  tenant: TenantDetail;
  thirdPartyBillingAccountSetDetail: ThirdPartyBillingAccountSetDetail;
  returnToAddressOverrides: ReturnToAddressOverrideDetail[];
}

export interface ReturnToAddressOverrideDetail {
  returnToAddressOverride: ReturnToAddressOverrides;
  returnToAddress: AddressDetail;
  returnToContact: ContactDetail;
}

export interface AddressDetail {
  address: PartnerAddressV2;
}

export interface ContactDetail {
  contact: PartnerContact;
}

export interface InjectionSiteDetail {
  tags: string[];
  injectionSite: InjectionSite;
  address: AddressDetail;
  contact: ContactDetail;
  contract: PartnerContractDetail;
  augmentationSet: AugmentationSet;
}
export interface InjectionProfileDetail {
  injectionProfile: InjectionProfile;
  injectionSites: InjectionSiteDetail[];
}
export interface AugmentationDetail {
  augmentation: Augmentation;
  tag: AssetTag[];
}

export interface CarrierAugmentation {
  labelAugmentation: {
    augmentation: Augmentation;
  };
  carrierId: string;
}

export interface CarrierServiceMethodAugmentations {
  carrierId: string;
  serviceMethodLabelAugmentation: ServiceMethodAugmentation[];
}

export interface ServiceMethodAugmentation {
  labelAugmentation: {
    augmentation: Augmentation;
  };
  serviceMethodId: string;
}

export interface AugmentationSet extends AuditAsset {
  augmentationSetId: string;
  augmentationIds: string[];
  carrierServiceMethodAugmentations: CarrierServiceMethodAugmentations[];
  carrierAugmentations: CarrierAugmentation[];
  augmentations: AugmentationDetail[];
}

export interface Augmentation extends AuditAsset {
  injectionPosition: string;
  labelAugmentationId: string;
  shipmentTagToMatch: string;
  zpl: string;
}

export interface FulfillmentContext {
  active: boolean;
  archived: boolean;
  auditInfo: AuditInfo;
  augmentationSetId?: string;
  contractIds?: string[];
  description?: string;
  domain?: FulfillmentContextDomain;
  fulfillmentContextId: string;
  fulfillmentType: 'at_large' | 'customer' | 'returns';
  injectionProfileIds?: string[];
  labelPropertiesId?: string;
  name: string;
  packageSetId?: string;
  partnerId: string;
  partnerProvidedId?: string;
  returnsSettingsId?: string;
  originId?: string;
  tagIds?: string[];
  thirdPartyBillingAccountSetIds?: string[];
  urn: string;
}

export interface LabelPropertiesBase {
  active: boolean;
  archived: boolean;
  dpi: number;
  width: number;
  height: number;
  labelOrientation: string;
  linearUnits: LinearUnit;
  name: string;
  partnerProvidedId?: string;
  nonZplLabelDimensions?: {
    height: number;
    width: number;
    linearUnits: LinearUnit;
  };
}

export interface LabelProperties extends LabelPropertiesBase {
  dimensions: string;
  auditInfo: AuditInfo;
  labelPropertiesId: string;
  partnerId: string;
  tagIds?: string[];
  urn: string;
}

export interface LabelPropertiesDetail {
  labelProperties: LabelProperties;
}

/* Contract */

export interface Contract {
  active: boolean;
  archived: boolean;
  auditInfo: AuditInfo;
  carrierAccountId?: string;
  carrierId: string;
  description?: string;
  carrierRateSetId: string;
  carrierSettingsId: string;
  contractId: string;
  labelPropertiesId?: string;
  name: string;
  partnerProvidedId?: string;
  partnerId: string;
  tagIds?: string[];
  urn: string;
}

export interface CarrierSettings {
  active: boolean;
  allowLabelGenerationFailover: boolean;
  archived: boolean;
  auditInfo: AuditInfo;
  carrierId: string;
  carrierSettingsId: string;
  description: string;
  expectedShippingVolumeOveragePercentage: number;
  expectedShippingVolumeOveragePercentageByGroup?: Record<string, number>;
  hazmatLimitedQuantityExempt: boolean;
  name: string;
  partnerId: string;
  partnerProvidedId: string;
  peakSurchargeEligible: boolean;
  tagIds: string[];
  urn: string;
}

export interface CarrierRate {
  carrierRate: PartnerCarrierRate;
}

export interface AveragePackageDimensions {
  averagePackageVolume?: number;
  linearUnit: LinearUnit;
}

export interface UPSRegistration {
  carrierAccountId: string;
  createdBy: string;
  creationTimestamp: string;
  expiration: string;
  lastUpdatedBy: string;
  lastUpdatedTimestamp: string;
  lifecycleStage: 'pending' | 'visited' | 'registered' | 'expired' | 'unregistered' | 'error';
  partnerId: string;
  tenantId?: string;
  upsRedirectUri: string;
  upsRegistrationId: string;
}

export interface FedexRegistration {
  addressValidatedAt?: string;
  carrierAccountId: string;
  createdAt: string;
  createdBy: string;
  fedexAuthCode?: string;
  fedexChildKey?: string;
  fedexChildSecret?: string;
  fedexRegistrationId: string;
  lastUpdatedAt: string;
  lastUpdatedBy: string;
  lifecycleStage:
    | 'pending'
    | 'addressValidated'
    | 'multiFactorValidated'
    | 'registered'
    | 'unregistered'
    | 'expired'
    | 'error';
  partnerId: string;
  pinGeneratedAt?: string;
  registerMethod?: 'invoice' | 'pin' | 'fedexSupport';
  registeredAt?: string;
  tenantId?: string;
}

export interface CarrierRegistrationEvent {
  carrierAccountId: string;
  createdAt: string;
  createdBy: string;
  eventDetail: string;
  eventId: string;
  eventMetaData: any;
  eventType: string;
  partnerId: string;
  registrationLookupId: string;
}

export interface CredentialDefinition {
  credentialStructureId: string;
  credentials: Record<string, string>;
}

export interface NegotiatedSurcharge {
  modifierType: 'credit' | 'percentage' | 'replacement' | 'replacement_percentage' | 'fixed_percentage_discount';
  modifierValue: number;
  name: string;
  surchargeId: string;
}

export interface RateTableDatum {
  [zone: string]: number;
}

export interface RateTableRates {
  [weight: string]: RateTableDatum;
}
export type WeightUnit = 'lb' | 'oz' | 'kg' | 'g';

/* Origin / Address */
export interface OriginDetail {
  address: {
    address: PartnerAddressV2;
    tags: AssetTag;
  };
  contact: {
    contact: PartnerContact;
    tags: AssetTag;
  };
  origin: PartnerOrigin;
  schedule: {
    schedule: Schedule;
    tags: AssetTag;
  };
  tags: AssetTag[];
}

export type LabelPropertiesCreateParams = LabelPropertiesBase;

export interface LabelPropertiesUpdateParams extends LabelPropertiesCreateParams {
  labelPropertiesId: string;
}

export interface AddressCreateParams {
  active: boolean;
  archived: boolean;
  name: string;
  location: Location;
}

export interface AddressUpdateParams extends AddressCreateParams {
  addressId: string;
}

export interface ContactCreateParams {
  active: boolean;
  archived: boolean;
  name: string;
  email: string;
  phoneNumber: string;
}

export interface ContactUpdateParams extends ContactCreateParams {
  contactId: string;
}

export interface ScheduleUpdateParams extends ScheduleCreateParams {
  scheduleId: string;
}

export interface ScheduleCreateParams {
  originId: string;
  active: boolean;
  archived: boolean;
  name: string;
  description?: string;
  entries: ScheduleEntry[];
  fulfillmentContextId: string;
}
export interface AddressLineComponents {
  streetName?: string;
  primaryAddressNumber?: string;
  secondaryAddressNumber?: string;
  district?: string;
  neighborhood?: string;
}

export interface AddressMetadata {
  latitude: number;
  longitude: number;
  timeZoneId: string;
}

export interface ReturnToAddressOverrides {
  partnerId: string;
  returnToAddressOverrideId: string;
  partnerProvidedId: string;
  simulationId: string;
  name: string;
  description: string;
  addressId: string;
  contactId: string;
  carrierIds: string[];
  tenantIds: string[];
  active: boolean;
  archived: boolean;
  auditInfo: AuditInfo;
  documentExpiration: string;
}

export interface Schedule {
  active: boolean;
  archived: boolean;
  auditInfo: AuditInfo;
  description: string;
  entries: ScheduleEntry[];
  name: string;
  partnerId: string;
  partnerProvidedId: string;
  scheduleId: string;
  tagIds: string[];
  urn: string;
}

export type shippingDay = 'FRIDAY' | 'MONDAY' | 'SATURDAY' | 'SUNDAY' | 'THURSDAY' | 'TUESDAY' | 'WEDNESDAY';

export interface ScheduleEntry {
  from: string;
  to: string;
  holidays: ScheduleHoliday[];
  // TODO remove this intersection once @shipium-corp/shipium-ts-types publishes the OTS fields on
  // PartnerProcessingCutoff. otsOverrideExpiration is an ISO instant, not a calendar date.
  processingCutoff: PartnerProcessingCutoff & {
    otsTagsEnabled?: boolean;
    otsOverrideDaysAndTimeToShip?: number | null;
    otsOverrideExpiration?: string | null;
  };
  shippingDays: shippingDay[];
  scheduleEntryId?: string;
  carrierSchedules?: CarrierSchedule[];
}

export interface CarrierSchedule {
  carrierId: string;
  carrierScheduleId?: string;
  carrierServiceMethodIds: string[];
  cutoffExceptions: CarrierScheduleCutoffException[];
  cutoffTimes: CarrierScheduleCutoffTime[];
  injectionSiteId?: string;
}

export interface CarrierScheduleCutoffException {
  cutoff?: string;
  date: string;
}

export interface CarrierScheduleCutoffTime {
  cutoff?: string;
  dayOfWeek: string;
}

export interface ScheduleHoliday {
  date: string;
  // Absent or empty when the holiday was written outside the Console (e.g. by Polaris): date only.
  metadata?: Record<string, string | undefined>;
}

/* Tenant */
export interface TenantLink {
  shipiumId: string;
}

export interface TenantOriginInfoMap {
  originId: string;
  returnToAddressId: string;
  returnToContactId: string;
}

export interface TenantDetail {
  originInfoDetailMap: any;
  tags: AssetTag;
  tenant: PartnerTenant;
  unitedStatesPrincipalPartyInInterestAddress: {
    address: PartnerAddressV2;
    tags: AssetTag;
  };
  unitedStatesPrincipalPartyInInterestContact: {
    contact: PartnerContact;
    tags: AssetTag;
  };
}

/* Shared */
export interface AssetTag {
  active: boolean;
  archived: boolean;
  auditInfo: AuditInfo;
  description?: string;
  name: string;
  partnerId: string;
  partnerProvidedId?: string;
  tag?: string;
  tagId: string;
  urn: string;
}

export interface AuditInfo {
  createdBy: string;
  creationTimeStamp: string;
  lastUpdatedBy: string;
  lastUpdatedTimeStamp: string;
  version: number;
}

export const OriginTypeDisplayMap: Record<string, string> = {
  at_large: 'At Large',
  fc: 'Fulfillment Center',
  store: 'Store',
};

export interface CarrierRateSet {
  partnerProvidedId: string;
  active: boolean;
  archived: boolean;
  auditInfo: AuditInfo;
  name: string;
  description: string;
  carrierRateSetId: string;
  carrierRateIds: string[];
}

// TODO Build out. This is just a minimum expression of the values
export interface SelectionModifier {
  active: boolean;
  archived: boolean;
  auditInfo: AuditInfo;
  effectiveDateTime: string;
  expirationDateTime: string;
  name: string;
  selectionModifierId: string;
}

export interface ReturnSetting {
  active: boolean;
  archived: boolean;
  auditInfo: AuditInfo;
  description: string;
  name: string;
  partnerId: string;
  partnerProvidedId?: string;
  returnToAddressId: string;
  returnToContactInfoId: string;
  returnsSettingsId: string;
  tagIds: string[];
  urn: string;
}

export interface Packaging {
  active: boolean;
  archived: boolean;
  auditInfo: AuditInfo;
  linearDimensions: {
    length: number;
    width: number;
    height: number;
    linearUnit: string;
  };
  maxContentWeight: {
    weight: number;
    weightUnit: string;
  };
  packagingMaterial: string;
  packagingSizeName: string;
  packagingId: string;
  packagingWeight: {
    weight: number;
    weightUnit: string;
  };
  partnerId: string;
  partnerProvidedId?: string;
  unsupportedProductDetails: string[];
}

export interface PackagingSet {
  active: boolean;
  archived: boolean;
  auditInfo: AuditInfo;
  description: string;
  name: string;
  packagingIds: string[];
  packagingSetId: string;
  partnerId: string;
  partnerProvidedId?: string;
  tagIds: string[];
  urn: string;
}

export interface ThirdPartyBillingAccount {
  active: boolean;
  archived: boolean;
  auditInfo: AuditInfo;
  carrierId: string;
  carrierServiceMethodIds: string[];
  name: string;
  description: string;
  partnerId: string;
  partnerProvidedId: string;
  tagIds: string[];
  thirdPartyBillingAccountId: string;
  thirdPartyCredentials: {
    credentialStructureId: string;
    credentials: Record<string, string>;
  };
  billThirdPartyForShippingCost?: boolean;
  billThirdPartyDutiesAndTaxes?: boolean;
  urn: string;
}

export interface ThirdPartyBillingAccountSetDetail {
  tags: AssetTag[];
  thirdPartyBillingAccounts: {
    tags: AssetTag[];
    thirdPartyBillingAccount: ThirdPartyBillingAccount;
  }[];
}

export interface UPSRegistrationCreate {
  carrierAccountId: string;
  tenantId?: string;
  partnerId: string;
}

export interface ValidateFedexAddressBody {
  accountNumber: {
    key: string;
    value: string;
  };
  address: {
    city: string;
    countryCode: string;
    postalCode: string;
    residential: boolean;
    stateOrProvinceCode: string;
    streetLines: Array<string>;
  };
  customerName: string;
  mockFedexChildKey?: string;
  mockFedexChildSecret?: string;
  usePastAddress?: boolean;
}

export const fedexLocales = [
  'ar_AE',
  'bg_BG',
  'zh_CN',
  'zh_HK',
  'zh_TW',
  'cs_CZ',
  'da_DK',
  'nl_NL',
  'en_CA',
  'en_GB',
  'en_US',
  'et_EE',
  'fi_FI',
  'fr_CA',
  'fr_FR',
  'de_DE',
  'de_CH',
  'el_GR',
  'hu_HU',
  'it_IT',
  'ja_JP',
  'ko_KR',
  'lv_LV',
  'lt_LT',
  'no_NO',
  'pl_PL',
  'pt_BR',
  'pt_PT',
  'ro_RO',
  'ru_RU',
  'sk_SK',
  'sl_SI',
  'es_AR',
  'es_MX',
  'es_ES',
  'es_US',
  'sv_SE',
  'th_TH',
  'tr_TR',
  'uk_UA',
  'vi_VN',
] as const;

export type FedexLocalesType = (typeof fedexLocales)[keyof typeof fedexLocales];

export interface ValidateFedexInvoiceBody {
  invoiceDetail: {
    amount: string;
    currency: string;
    date: string;
    number: string;
  };
  locale: FedexLocalesType;
}

export interface ValidateFedexPinBody {
  secureCodePin: string;
  locale: FedexLocalesType;
}

export interface GenerateFedexPinBody {
  option: 'CALL' | 'EMAIL' | 'SMS';
  locale: FedexLocalesType;
}

export const FEDEX_SHIPIUM_API_KEY = 'fedex-shipium-api-key';
export const FEDEX_SHIPIUM_API_PASSWORD = 'fedex-shipium-api-password';
export const FEDEX_ACCOUNT_NUMBER = 'fedex-account-number';
export const FEDEX_CHILD_API_KEY = 'fedex-child-api-key';
export const FEDEX_CHILD_API_SECRET = 'fedex-child-api-secret';
export const FEDEX_REST_USAGE_AMOUNT = 'fedex-rest-usage-amount';
export const FEDEX_SHIPIUM_ACCOUNT_NUMBER = 'fedex-shipium-account-number';
export const FEDEX_SHIPIUM_METER_NUMBER = 'fedex-shipium-meter-number';
